import {
  PointerEvent as ReactPointerEvent,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useOverlayPortalContainer } from '../OverlayPortalContainer/overlayPortalContainer';

// Tailwind's `md` breakpoint (48rem). Drag is desktop-only, so we gate it on
// the same threshold Tailwind uses for `md:`.
const MD_BREAKPOINT_QUERY = '(min-width: 48rem)';

/**
 * True once the viewport is at least Tailwind's `md` breakpoint wide.
 *
 * Preferred approach is the app's `useIsContainerBeyondBreakpoint('md', {
 * container: 'document' })`, but that hook lives in `apps/webapp-provider` and
 * Ambrosia is a shared package — importing an app into a package would invert
 * the dependency direction. This local `matchMedia` fallback mirrors the same
 * `md` threshold instead.
 */
export const useIsMdBreakpoint = (): boolean => {
  const [isMd, setIsMd] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(MD_BREAKPOINT_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia(MD_BREAKPOINT_QUERY);
    setIsMd(mql.matches);
    const handler = (event: MediaQueryListEvent) => setIsMd(event.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  return isMd;
};

// Standard clamp, but bail on an inverted range (min > max) — that happens when
// the popup is wider/taller than its container, where clamping would otherwise
// snap the offset to the wrong bound and make the popup jump.
const clamp = (value: number, min: number, max: number): number => {
  if (min > max) return value;
  return Math.min(Math.max(value, min), max);
};

export interface UseModalDragOptions {
  isOpen: boolean;
  draggable: boolean;
  /** Breakpoint gate — drag is only enabled on desktop (`md` and up). */
  enabled: boolean;
}

export interface UseModalDragResult {
  ref: RefObject<HTMLDivElement | null>;
  onPointerDown?: (event: ReactPointerEvent<HTMLElement>) => void;
}

/**
 * Pointer-driven drag for a Base UI `Dialog.Popup`.
 *
 * The offset is written imperatively to `--modal-drag-x` / `--modal-drag-y`
 * custom properties on the popup, so a drag never triggers a React re-render.
 * The popup composes those into its `translate` (drag + centring); the
 * open/close animation lives on `scale`/`opacity` — a separate CSS property in
 * Tailwind v4 — so the two never fight.
 *
 * Spread the returned `ref` and `onPointerDown` onto `Dialog.Popup`.
 */
export const useModalDrag = (
  options: UseModalDragOptions
): UseModalDragResult => {
  const { isOpen, draggable, enabled } = options;

  const containerRef = useOverlayPortalContainer();
  const popupRef = useRef<HTMLDivElement>(null);
  // Committed offset from centre, persisted across successive drags while open.
  const offsetRef = useRef({ x: 0, y: 0 });

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (event.button !== 0) return;

      const target = event.target as Element;
      if (!target.closest('[data-modal-drag-handle]')) return;
      // Let header buttons/inputs receive the pointer so their clicks fire.
      // Only a bare handle press starts a drag.
      if (target.closest("button, input, select, textarea, a, [role='button']"))
        return;

      const popup = popupRef.current;
      if (!popup) return;

      // React bubbles synthetic events through the component tree, and a nested
      // modal is a React child of the one it opened over. So a press on a nested
      // modal's handle also reaches this (ancestor) handler — but the two popups
      // are separate portalled DOM subtrees. Only drag when the press lands on
      // THIS popup's own handle, otherwise the parent would hijack the child.
      if (!popup.contains(target)) return;

      // A real drag starts here: stop header text selection while keeping the
      // header buttons above clickable.
      event.preventDefault();

      const popupRect = popup.getBoundingClientRect();
      const containerEl = containerRef?.current ?? document.documentElement;
      const containerRect = containerEl.getBoundingClientRect();

      const startX = event.clientX;
      const startY = event.clientY;
      const startOffsetX = offsetRef.current.x;
      const startOffsetY = offsetRef.current.y;
      const { pointerId } = event;

      // Clamp bounds relative to the drag start, so the popup stays inside the
      // container rect on both axes.
      const minDX = containerRect.left - popupRect.left;
      const maxDX = containerRect.right - popupRect.right;
      const minDY = containerRect.top - popupRect.top;
      const maxDY = containerRect.bottom - popupRect.bottom;

      let committedX = startOffsetX;
      let committedY = startOffsetY;

      const handleMove = (moveEvent: PointerEvent) => {
        if (moveEvent.pointerId !== pointerId) return;
        const dx = clamp(moveEvent.clientX - startX, minDX, maxDX);
        const dy = clamp(moveEvent.clientY - startY, minDY, maxDY);
        committedX = startOffsetX + dx;
        committedY = startOffsetY + dy;
        popup.style.setProperty('--modal-drag-x', `${committedX}px`);
        popup.style.setProperty('--modal-drag-y', `${committedY}px`);
      };

      const handleEnd = (endEvent: PointerEvent) => {
        if (endEvent.pointerId !== pointerId) return;
        offsetRef.current = { x: committedX, y: committedY };
        popup.releasePointerCapture?.(pointerId);
        popup.removeEventListener('pointermove', handleMove);
        popup.removeEventListener('pointerup', handleEnd);
        popup.removeEventListener('pointercancel', handleEnd);
      };

      popup.setPointerCapture(pointerId);
      popup.addEventListener('pointermove', handleMove);
      popup.addEventListener('pointerup', handleEnd);
      popup.addEventListener('pointercancel', handleEnd);
    },
    [containerRef]
  );

  // Reset the offset when the modal closes or reopens, so a re-opened modal is
  // centred again.
  useEffect(() => {
    offsetRef.current = { x: 0, y: 0 };
    const popup = popupRef.current;
    if (popup) {
      popup.style.removeProperty('--modal-drag-x');
      popup.style.removeProperty('--modal-drag-y');
    }
  }, [isOpen]);

  return {
    ref: popupRef,
    onPointerDown: draggable && enabled ? onPointerDown : undefined,
  };
};
